import React, { useEffect, useState } from 'react';
import {
  Button,
  Modal,
  Form,
  Container,
  Row,
  Col,
  Table,
  Card,
} from 'react-bootstrap';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import DeleteDialog from '../../components/DeleteDialog';
import toast from 'react-hot-toast';
import { Formik, Field, Form as FormikForm, ErrorMessage } from 'formik';
import * as Yup from 'yup';

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Validation: no empty, spaces only, or leading/trailing spaces
const noEmptyOrSpaces = (msg) =>
  Yup.string()
    .required(msg)
    .test(
      'no-empty-or-spaces',
      'Cannot be empty or spaces only',
      (value) => value && value.trim().length > 0 && !/^\s+$/.test(value)
    )
    .min(2, 'Too short');

const subHospitalValidation = Yup.object().shape({
  sub_hospital_name: noEmptyOrSpaces('Sub-Hospital Name is required'),
  location: noEmptyOrSpaces('Location is required'),
  code: Yup.string()
    .required('Code is required')
    .test(
      'no-empty-or-spaces',
      'Cannot be empty or spaces only',
      (value) => value && value.trim().length > 0 && !/^\s+$/.test(value)
    )
    .min(1, 'Code is too short'),
});

// Prevent leading spaces and pure spaces input
const preventSpaces = (e) => {
  if (
    e.key === ' ' &&
    (!e.target.value || /^\s*$/.test(e.target.value + e.key))
  ) {
    e.preventDefault();
  }
};
const preventPasteSpaces = (e) => {
  if (/^\s*$/.test(e.clipboardData.getData('text'))) {
    e.preventDefault();
  }
};

const Subhospital = () => {
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [subHospitals, setSubHospitals] = useState([]);
  const [editSubHospital, setEditSubHospital] = useState(null);
  const [error, setError] = useState(null);

  const hospitalId = (() => {
    try {
      const userObj = JSON.parse(localStorage.getItem('user'));
      return userObj && userObj.hospital_id ? userObj.hospital_id : '';
    } catch {
      return '';
    }
  })();

  // Fetch all sub-hospitals for the hospital
  const fetchSubHospitals = async () => {
    if (!hospitalId) return;
    try {
      const response = await axios.get(
        `${BASE_URL}/api/subHospitals/getSubHospitalsByHospital/${hospitalId}`,
      );
      setSubHospitals(response.data);
    } catch (error) {
      setError('Error fetching sub-hospitals');
    }
  };

  useEffect(() => {
    fetchSubHospitals();
    // eslint-disable-next-line
  }, [hospitalId]);

  // Open modal for add/edit
  const handleAddClick = () => {
    setEditSubHospital(null);
    setShow(true);
    setError(null);
  };
  const handleEditClick = (subHospital) => {
    setEditSubHospital(subHospital);
    setShow(true);
    setError(null);
  };

  // Submission handler (Formik)
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setError(null);
    const payload = {
      ...values,
      hospital_id: hospitalId,
      ...(editSubHospital?.sub_hospital_id && {
        sub_hospital_id: editSubHospital.sub_hospital_id,
      }),
    };
    try {
      if (editSubHospital?.sub_hospital_id) {
        await axios.put(`${BASE_URL}/api/subHospitals/editSubHospital`, payload);
        toast.success('Sub-hospital updated successfully!');
      } else {
        await axios.post(`${BASE_URL}/api/subHospitals/addSubHospital`, payload);
        toast.success('Sub-hospital added successfully!');
      }
      setShow(false);
      fetchSubHospitals();
      resetForm();
    } catch (error) {
      const errorMsg =
        error.response?.data?.message ||
        'Failed to save sub-hospital. Please try again.';
      setError(errorMsg);
      toast.error(errorMsg);
    }
    setSubmitting(false);
  };

  return (
    <div className="d-flex">
      {/* <Sidebar /> */}
      <Container fluid py-4>
        <Row className="mb-4 pt-5  align-items-center">
          <Col>
            <h3 className="global-heading mb-0">
              Sub-Hospital Management
            </h3>
          </Col>
          <Col xs="auto" className="text-end">
            <Button
              className="global-btn fw-normal d-flex align-items-center"
              size="sm"
              onClick={handleAddClick}
            >
              Add Sub-Hospital
            </Button>
          </Col>
        </Row>
        <Card className="shadow-sm border-0 mb-3">
          <Card.Body>
            <Table striped hover responsive className="align-middle mb-0">
              <thead className="bg-primary text-white">
                <tr>
                  <th>Sub-Hospital Name</th>
                  <th>Location</th>
                  <th>Hospital ID</th>
                  <th>Code</th>
                  <th style={{ minWidth: 120 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {subHospitals.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center text-muted">
                      No sub-hospitals found.
                    </td>
                  </tr>
                ) : (
                  subHospitals.map((subHospital) => (
                    <tr key={subHospital.sub_hospital_id}>
                      <td>{subHospital.sub_hospital_name}</td>
                      <td>{subHospital.location}</td>
                      <td>{subHospital.hospital_id}</td>
                      <td>{subHospital.code}</td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button
                            onClick={() => handleEditClick(subHospital)}
                            size="sm"
                            variant="light"
                            className="fw-semibold primary-class"
                          >
                            <span className="material-icons me-1" style={{ fontSize: 16 }}>
                              edit
                            </span>
                          </Button>
                          <DeleteDialog
                            path={`${BASE_URL}/api/subHospitals/deleteSubHospital/${subHospital.sub_hospital_id}`}
                            message="Sub-hospital deleted successfully"
                            onSuccess={fetchSubHospitals}
                            icon="button"
                            btnProps={{
                              size: "sm",
                              variant: "light",
                              className: "fw-normal d-flex align-items-center",
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        {/* Modal for Add/Edit Sub-Hospital */}
        <Modal
          show={show}
          onHide={() => setShow(false)}
          centered
          backdrop="static"
          className="form-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title className="global-heading fs-5">
              {editSubHospital?.sub_hospital_id
                ? 'Edit Sub-Hospital'
                : 'Add Sub-Hospital'}
            </Modal.Title>
          </Modal.Header>
          <Formik
            enableReinitialize
            initialValues={{
              sub_hospital_name: editSubHospital?.sub_hospital_name || '',
              location: editSubHospital?.location || '',
              code: editSubHospital?.code || '',
            }}
            validationSchema={subHospitalValidation}
            onSubmit={handleSubmit}
          >
            {({ handleSubmit, isSubmitting }) => (
              <FormikForm onSubmit={handleSubmit}>
                <Modal.Body>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold primary-class">
                      Sub-Hospital Name
                    </Form.Label>
                    <Field
                      name="sub_hospital_name"
                      as={Form.Control}
                      placeholder="Enter sub-hospital name"
                      required
                      onKeyDown={preventSpaces}
                      onPaste={preventPasteSpaces}
                    />
                    <ErrorMessage
                      name="sub_hospital_name"
                      component="div"
                      className="text-danger small"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold primary-class">
                      Location
                    </Form.Label>
                    <Field
                      name="location"
                      as={Form.Control}
                      placeholder="Enter location"
                      required
                      onKeyDown={preventSpaces}
                      onPaste={preventPasteSpaces}
                    />
                    <ErrorMessage
                      name="location"
                      component="div"
                      className="text-danger small"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold primary-class">
                      Code
                    </Form.Label>
                    <Field
                      name="code"
                      as={Form.Control}
                      placeholder="Enter code"
                      required
                      onKeyDown={preventSpaces}
                      onPaste={preventPasteSpaces}
                    />
                    <ErrorMessage
                      name="code"
                      component="div"
                      className="text-danger small"
                    />
                  </Form.Group>
                  {error && (
                    <div className="text-danger mb-3 fw-semibold">{error}</div>
                  )}
                  <Button
                    type="submit"
                    className="global-btn w-100 fw-normal d-flex align-items-center justify-content-center"
                    size="sm"
                    disabled={isSubmitting}
                  >
                    <span className="material-icons me-1" style={{ fontSize: 16 }}>
                      {editSubHospital?.sub_hospital_id ? "check_circle" : "add_circle"}
                    </span>
                    {editSubHospital?.sub_hospital_id
                      ? 'Update Sub-Hospital'
                      : 'Add Sub-Hospital'}
                  </Button>
                </Modal.Body>
              </FormikForm>
            )}
          </Formik>
        </Modal>
      </Container>
    </div>
  );
};

export default Subhospital;